import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, dateTime } from "@/lib/api";
import { COLORS } from "@/lib/colors";
import { Btn, inputCls } from "./ui";

export default function InventoryDialog({ product, onClose, onSaved }) {
  const [stock, setStock] = useState({});
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!product) return;
    setStock(Object.fromEntries(product.colors.map((c) => [c.name, c.stock])));
    api.get("/admin/inventory/history", { params: { product_id: product.id, limit: 30 } }).then((r) => setHistory(r.data));
  }, [product]);

  const save = async () => {
    setSaving(true);
    try {
      for (const c of product.colors) {
        if (Number(stock[c.name]) !== c.stock) {
          await api.patch(`/admin/products/${product.id}/stock`, { color: c.name, stock: Number(stock[c.name]) });
        }
      }
      toast.success("Stock updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="admin max-w-lg min-h-0 bg-white" data-testid="inventory-dialog">
        <DialogHeader><DialogTitle>Inventory — {product?.name}</DialogTitle></DialogHeader>
        {product?.colors.length === 0 ? (
          <p className="text-sm text-zinc-500">This product has no colors yet. Open the product to add colors.</p>
        ) : (
          <div className="space-y-2">
            {product?.colors.map((c) => (
              <div key={c.name} className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-2 text-sm"><span className="w-4 h-4 rounded-full border border-zinc-300" style={{ background: COLORS[c.name] }} />{c.name}</span>
                <input type="number" min={0} className={`${inputCls} w-28`} value={stock[c.name] ?? 0}
                  onChange={(e) => setStock({ ...stock, [c.name]: Math.max(0, parseInt(e.target.value || "0", 10)) })} data-testid={`inventory-stock-${c.name.toLowerCase()}`} />
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="secondary" onClick={onClose} data-testid="inventory-cancel">Cancel</Btn>
          <Btn onClick={save} disabled={saving} data-testid="inventory-save">Save stock</Btn>
        </div>
        <div className="border-t border-zinc-200 pt-4">
          <p className="text-[13px] font-semibold mb-2">Recent stock changes</p>
          <div className="max-h-48 overflow-y-auto text-xs divide-y divide-zinc-100" data-testid="inventory-history">
            {history.length === 0 && <p className="text-zinc-500 py-2">No changes yet.</p>}
            {history.map((h) => (
              <div key={h.id} className="flex justify-between py-1.5 gap-3">
                <span className="text-zinc-500 w-28 flex-shrink-0">{dateTime(h.created_at)}</span>
                <span className="flex-1">{h.color} · {h.reason}</span>
                <span className={h.change > 0 ? "text-emerald-600" : "text-red-600"}>{h.change > 0 ? "+" : ""}{h.change}</span>
                <span className="text-zinc-500 w-10 text-right">= {h.new_stock}</span>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
